"use client";

import { getSession } from "next-auth/react";
import { AUTH_BASE_PATH, REFRESH_ERROR } from "@/lib/auth/oidc/constants";

/**
 * Access-token plumbing for the OIDC provider (Keycloak, Okta, Entra ID…).
 *
 * The browser never holds the refresh token, so it cannot mint a new access
 * token itself. What it can do is ask the Auth.js session endpoint, which runs
 * the jwt callback server-side and refreshes if the token is close to expiry.
 * Everything here exists to do that as rarely as is safe.
 */

export { AUTH_BASE_PATH, REFRESH_ERROR };

/**
 * Treat a token as spent this many seconds early.
 *
 * Larger than the server's own refresh headroom on purpose: if the client
 * decided a token was still good at the same instant the server did, a request
 * leaving here could still arrive at the backend after expiry.
 */
const EXPIRY_SKEW_SECONDS = 90;

/** The client-visible session, as the session callback in ./server.ts shapes it. */
export interface OidcSession {
  accessToken?: string;
  sessionId?: string;
  error?: string;
  /**
   * RP-initiated logout URL, built server-side. Absent when there is no IdP
   * session to end or the IdP publishes no end_session_endpoint.
   */
  endSessionUrl?: string;
  /** Issuer-qualified identity, `${issuer}#${subject}`. */
  externalUserId?: string;
}

/**
 * Claims from a JWT payload, without verifying it.
 *
 * Safe only because of what it is used for: reading `exp` to decide when to
 * refresh, and surfacing claims for display. The token is verified by the
 * backend, which is the only place a verification result can be trusted — a
 * browser checking a signature it also received proves nothing.
 */
export function decodeJwtClaims(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function expiresSoon(token: string): boolean {
  const claims = decodeJwtClaims(token);
  const exp = claims?.exp;
  // An undecodable or exp-less token is treated as expired: re-fetching is
  // cheap, and handing the backend something we cannot reason about is not.
  if (typeof exp !== "number") return true;
  return Date.now() / 1000 >= exp - EXPIRY_SKEW_SECONDS;
}

let cached: OidcSession | null = null;
// Collapses concurrent callers onto one request. Without it a page mounting
// several data hooks at once fires a session fetch per hook.
let inflight: Promise<OidcSession | null> | null = null;

async function fetchSession(): Promise<OidcSession | null> {
  inflight ??= (async () => {
    try {
      return (await getSession()) as OidcSession | null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Send the browser somewhere. The one place this package navigates.
 *
 * A function rather than an inline `window.location.href = url` so sign-out is
 * testable: jsdom marks both `location` and its `href` non-configurable, so
 * neither can be stubbed, and without this seam the assertion that logout
 * actually reaches the IdP could not be written at all.
 */
export function redirectTo(url: string): void {
  window.location.href = url;
}

/** Drop the cached session. Call on sign-out so the next read starts clean. */
export function clearOidcSessionCache(): void {
  cached = null;
}

/**
 * The access token requests are currently being sent with, without fetching.
 *
 * For code that reacts to a request's outcome and needs to know which
 * credential produced it; getOidcAccessToken() would refresh a near-expiry
 * token and answer with a different one.
 */
export function cachedOidcAccessToken(): string | null {
  return cached?.accessToken ?? null;
}

export async function getOidcSession(): Promise<OidcSession | null> {
  if (
    cached?.accessToken &&
    !cached.error &&
    !expiresSoon(cached.accessToken)
  ) {
    return cached;
  }
  cached = await fetchSession();
  return cached;
}

/**
 * The bearer token for the backend, or null when there is no usable session.
 *
 * Returns null rather than a stale token when refresh has failed, so callers
 * get a clean 401 they can act on instead of a confusing one from a token the
 * server already knows is dead.
 */
export async function getOidcAccessToken(): Promise<string | null> {
  const session = await getOidcSession();
  if (!session || session.error === REFRESH_ERROR) return null;
  return session.accessToken ?? null;
}
